import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, ShieldCheck, Zap, Sparkles, Database, Globe } from "lucide-react";
import LiveCounters from "../components/LiveCounters";
import InlineSnapshotWidget from "../components/InlineSnapshotWidget";
import CaseStudyLink from "../components/CaseStudyLink";
import { trackEvent } from "../lib/analytics";

/**
 * V232.7 — Comparison SEO page: TopRankPulse vs Mangools (KWFinder).
 *
 * Mangools is a Slovak-born (!) SEO suite — founded in Prešov by Peter
 * Hrbáček, now based in Bratislava + Košice. Flagship is KWFinder plus
 * SERPChecker / SERPWatcher / LinkMiner / SiteProfiler. Super-popular
 * entry-level alternative to Semrush with simple UX and ~€29-€79/mes.
 *
 * Why this comparator matters:
 *   • "mangools alternative", "kwfinder alternative" long-tail = ~500-800/mo
 *   • Mangools audience = exactly our ICP (small SK/CZ marketers who
 *     already understand "we use SK-built tools"). Perfect capture.
 *   • Mangools has ZERO AI SEO features — great differentiation hook.
 *
 * Tone: extra-respectful. Mangools is a Slovak success story and we
 * shouldn't appear to trash them. Angle = "Mangools made SEO
 * accessible; TopRankPulse makes AI SEO accessible — same spirit,
 * next generation".
 */
export default function CompareMangoolsPage() {
  const [faqOpen, setFaqOpen] = useState(null);
  const canonical = "https://toprankpulse.com/compare/toprankpulse-vs-mangools";

  useEffect(() => {
    trackEvent("view_compare_mangools", {
      competitor: "mangools",
      page_type: "versus",
    });
  }, []);

  const faqs = [
    {
      q: "Čo je lepšie — TopRankPulse alebo Mangools?",
      a: "Mangools (KWFinder + SERPChecker + SERPWatcher + LinkMiner) je skvelý affordable klasický SEO suite — jednoducho použiteľný keyword research a SERP tracking. TopRankPulse je AI-first platforma s meraním ChatGPT/Perplexity/Gemini citácií, čo Mangools k 02/2026 nemá. Odporúčanie: pre klasický SEO Mangools, pre AI SEO TopRankPulse. Mnoho SK/CZ tímov používa oba paralelne.",
    },
    {
      q: "Koľko stojí Mangools a koľko TopRankPulse?",
      a: "Mangools Basic plán €29/mes, Premium €49/mes, Agency plán €79/mes (fakturované ročne; mesačne je drahšie). TopRankPulse Pro €49/mes neobmedzene domén. Pre menšie projekty je Mangools lacnejší, pre agency s viac klientami je TopRankPulse Pro výhodnejší vďaka neobmedzenému počtu domén.",
    },
    {
      q: "Je Mangools slovenský nástroj?",
      a: "Áno — Mangools založil Peter Hrbáček v Prešove, firma má sídla v Bratislave a Košiciach. Ide o jeden z najväčších slovenských SaaS success stories (~50 000 platiacich zákazníkov globálne). Toto porovnanie je medzi dvoma slovenskými SEO nástrojmi — Mangools zavedený global brand, TopRankPulse AI-first newcomer so SK/CZ fokusom.",
    },
    {
      q: "Má Mangools AI SEO funkcie?",
      a: "K 02/2026 Mangools nemá AI citation tracking, FAQPage/HowTo schema check pre AEO ani llms.txt detekciu. Ich roadmap sa primárne točí okolo zdokonaľovania klasického keyword a backlink stacku. TopRankPulse je od začiatku AI-first — meranie citácií v 6 AI modeloch je naša core funkcia.",
    },
    {
      q: "Dá sa Mangools a TopRankPulse používať spoločne?",
      a: "Áno — mnoho našich klientov používa KWFinder na keyword research + TopRankPulse na AI citation monitoring. Spoločný náklad €29+€49 = €78/mes stále podstatne pod cenou Semrushu (€220/mes) a pokryje klasický aj AI SEO stack.",
    },
    {
      q: "Prečo si zvoliť TopRankPulse namiesto Mangools?",
      a: "Hlavne pre AI vrstvu — ak ti záleží na tom koľkokrát ťa cituje ChatGPT/Perplexity/Gemini pri odpovediach, Mangools ti to neukáže. Druhý dôvod: TopRankPulse má programmatic SEO landing pages pre každú SK/CZ firmu podľa IČO (ORSF + ARES), čo Mangools neposkytuje.",
    },
  ];

  const comparisonRows = [
    { feature: "Cena Basic plan", tp: "€7 jednorazový / €49/mes", mg: "€29/mes Basic (ročne)", winner: "mg" },
    { feature: "Cena Standard plan", tp: "€49/mes neobmedzene domén", mg: "€49/mes Premium (limitované keywords)", winner: "tp" },
    { feature: "Cena Agency plan", tp: "€149/mes white-label", mg: "€79/mes Agency", winner: "mg" },
    { feature: "AI citation tracking (ChatGPT/Perplexity/Gemini)", tp: true, mg: false, winner: "tp" },
    { feature: "FAQPage + HowTo schema (AEO)", tp: true, mg: false, winner: "tp" },
    { feature: "llms.txt detekcia", tp: true, mg: false, winner: "tp" },
    { feature: "Keyword research databáza (globálna)", tp: "~5M SK/CZ fráz", mg: "2.5+ miliárd global", winner: "mg" },
    { feature: "SERP tracker (SERPWatcher)", tp: "základný", mg: "pokročilý (dedicated SERPWatcher)", winner: "mg" },
    { feature: "Backlink analýza (LinkMiner)", tp: "základná", mg: "dedikovaný LinkMiner nástroj", winner: "mg" },
    { feature: "Site audit (SiteProfiler)", tp: "AI-first audit", mg: "klasický SiteProfiler", winner: "tie" },
    { feature: "Programmatic SEO landing pages", tp: "153 miest + 1000+ firiem", mg: false, winner: "tp" },
    { feature: "Živá SK/CZ firemná databáza (ORSF + ARES)", tp: true, mg: false, winner: "tp" },
    { feature: "Mesačný AI audit (self-serve, 10 min)", tp: true, mg: false, winner: "tp" },
    { feature: "SK UI + SK podpora", tp: "plne SK", mg: "SK podpora, EN UI", winner: "tp" },
    { feature: "GDPR + EU-hosting", tp: true, mg: true, winner: "tie" },
    { feature: "API prístup", tp: true, mg: "iba v Agency pláne", winner: "tp" },
    { feature: "UX jednoduchosť pre začiatočníkov", tp: "AI-focused, nový koncept", mg: "legendárne jednoduché UI", winner: "mg" },
    { feature: "Záruka vrátenia peňazí", tp: "14 dní", mg: "48h trial zdarma", winner: "tp" },
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "TopRankPulse AI SEO Audit",
    description:
      "AI-first SEO audit platforma pre SK + CZ firmy. Reálne odhady ChatGPT, Perplexity a Gemini citácií za €7, mesačný AI monitoring za €49.",
    brand: { "@type": "Brand", name: "TopRankPulse" },
    offers: {
      "@type": "Offer",
      price: "7.00",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: "https://toprankpulse.com/snapshot",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "3874",
    },
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>TopRankPulse vs Mangools (KWFinder) — Dva slovenské SEO nástroje 2026</title>
        <meta
          name="description"
          content="TopRankPulse vs Mangools: AI SEO audit vs klasický keyword research + SERP tracker. €49/mes vs €29-79/mes. Objektívne porovnanie 18 funkcií dvoch slovenských SEO nástrojov."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="TopRankPulse vs Mangools (KWFinder) — Dva slovenské SEO nástroje 2026" />
        <meta property="og:description" content="AI SEO audit vs klasický keyword research + SERP tracker. €49/mes vs €29-79/mes. Porovnanie 18 funkcií." />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="sk_SK" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="px-6 pt-16 pb-8 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#FFE600] mb-4 font-['JetBrains_Mono']">
          <span>Porovnanie 2026</span>
          <span className="opacity-50">·</span>
          <span className="text-gray-400">Aktualizované {new Date().toLocaleDateString("sk-SK")}</span>
        </div>

        <h1
          data-testid="compare-h1"
          className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] leading-tight mb-5 tracking-tight"
        >
          TopRankPulse <span className="text-gray-500">vs</span>{" "}
          <span className="text-[#ff6b35]">Mangools</span>
          <br />
          <span className="text-2xl sm:text-3xl text-gray-300 font-bold">
            Dva slovenské SEO nástroje — klasický vs AI-first
          </span>
        </h1>

        <p className="text-lg text-gray-300 mb-6 max-w-2xl leading-relaxed" data-testid="compare-tldr">
          <strong className="text-white">TL;DR:</strong> Mangools (KWFinder + 4 ďalšie moduly) je{" "}
          <strong className="text-white">slovenská SEO legenda</strong> pre jednoduchý klasický
          keyword research od €29/mes. TopRankPulse je{" "}
          <strong className="text-[#a855f7]">slovenský AI-first newcomer</strong> s meraním
          AI citácií za €49/mes. Z 18 porovnaných funkcií vyhráva TopRankPulse 10,
          Mangools 6, remíza 2. Rodený doplnok, nie konkurent.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="compare-cta-row">
          <Link
            to="/snapshot"
            onClick={() => trackEvent("compare_cta_click", { destination: "/snapshot", competitor: "mangools" })}
            className="inline-flex items-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-white font-black px-6 py-3 rounded-lg transition-all"
            data-testid="compare-cta-primary"
          >
            Skúsiť TopRankPulse za €7 <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="#comparison-table"
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold px-6 py-3 rounded-lg transition-all"
            data-testid="compare-cta-secondary"
          >
            Prejsť na porovnanie
          </a>
        </div>

        <LiveCounters variant="dark" />

        <div className="mt-6" data-testid="compare-inline-widget-wrap">
          <InlineSnapshotWidget
            source="compare_mangools_hero_widget"
            variant="dark"
            headline={
              <>
                Zisti sám — <span className="text-[#a855f7]">AI vrstvu, ktorú Mangools nemá</span>
              </>
            }
            subcopy="Mangools ti ukáže keyword difficulty. TopRankPulse ti ukáže, koľkokrát ťa cituje ChatGPT, Perplexity a Gemini. 14-dňová záruka vrátenia peňazí."
            ctaLabel="Spustiť môj audit za €7"
          />
        </div>
      </section>

      {/* QUICK VERDICT CARDS */}
      <section className="px-6 py-8 max-w-4xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="compare-verdict-cards">
          <VerdictCard
            testid="verdict-tp"
            variant="win"
            brand="TopRankPulse"
            price="od €7 / jednorazový · €49/mes neobmedzene"
            best="Pre firmy ktoré chcú merať AI viditeľnosť + majú záujem o programmatic SEO"
            points={[
              "AI citation tracking v 6 modeloch",
              "Programmatic SEO (153 miest + 1000+ firiem)",
              "10-minútový self-serve audit",
              "SK/CZ firemná databáza (ORSF + ARES)",
              "14-dňová záruka vrátenia peňazí",
            ]}
            accent="#a855f7"
          />
          <VerdictCard
            testid="verdict-mg"
            variant="neutral"
            brand="Mangools (KWFinder)"
            price="€29-79/mes podľa plánu"
            best="Pre začiatočníkov a solo marketérov hľadajúcich jednoduché klasické SEO"
            points={[
              "KWFinder (keyword research s UX legendárnou jednoduchosťou)",
              "SERPChecker a SERPWatcher dedicated nástroje",
              "LinkMiner pre backlink analýzu",
              "2.5+ miliárd global keyword databáza",
              "Založené v Prešove — slovenská success story",
            ]}
            accent="#ff6b35"
          />
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section id="comparison-table" className="px-6 py-12 max-w-4xl mx-auto">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6" data-testid="compare-h2-table">
          Porovnanie 18 funkcií
        </h2>
        <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/5">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead className="bg-black/40">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-gray-300">Funkcia</th>
                <th className="text-left px-4 py-3 font-bold text-[#a855f7]">TopRankPulse</th>
                <th className="text-left px-4 py-3 font-bold text-[#ff6b35]">Mangools</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, i) => (
                <tr key={row.feature} className={i % 2 ? "bg-white/[0.02]" : ""} data-testid={`compare-row-${i}`}>
                  <td className="px-4 py-3 text-gray-200">{row.feature}</td>
                  <td className={`px-4 py-3 font-semibold ${row.winner === "tp" ? "text-emerald-400" : "text-gray-200"}`}>
                    <Cell value={row.tp} highlight={row.winner === "tp"} />
                  </td>
                  <td className={`px-4 py-3 ${row.winner === "mg" ? "text-emerald-400 font-semibold" : "text-gray-400"}`}>
                    <Cell value={row.mg} highlight={row.winner === "mg"} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-black/40 border-t border-white/10">
              <tr>
                <td className="px-4 py-3 font-bold text-gray-200">Celkové skóre</td>
                <td className="px-4 py-3 font-black text-emerald-400">10 výhier · 2 remízy</td>
                <td className="px-4 py-3 text-gray-300">6 výhier · 2 remízy · 10 strát</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="text-xs text-gray-500 mt-3" data-testid="compare-disclaimer">
          Zdroj cien: verejne dostupný cenník Mangools k 02/2026 (mangools.com/pricing).
          Porovnanie prebieha v súlade s EU Directive 2006/114/EC — všetky tvrdenia overiteľné.
          Pozn.: Mangools je slovenská spoločnosť založená v Prešove — toto porovnanie je medzi
          dvoma slovenskými SEO nástrojmi s rozdielnym fókusom (klasický vs AI).
        </p>
      </section>

      {/* CASE STUDY CROSS-LINK */}
      <CaseStudyLink competitorKey="mangools" />

      {/* WHY COMPLEMENT */}
      <section className="px-6 py-12 max-w-4xl mx-auto" data-testid="compare-why-switch">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6">
          Kedy voliť ktorý nástroj (alebo oba spolu)?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReasonCard
            icon={Sparkles}
            title="AI vrstva, ktorú Mangools nemá"
            body="Mangools je klasický SEO suite — SERP, kľúčové slová, backlinky. TopRankPulse meria niečo iné: koľkokrát ťa citujú ChatGPT, Perplexity a Gemini. Rozdielny signál."
          />
          <ReasonCard
            icon={Zap}
            title="Programmatic SEO landing pages"
            body="153 SK+CZ miest + tisíce firiem s dedikovanými landing stránkami podľa IČO (ORSF + ARES). Mangools takú funkciu neposkytuje — výborný free traffic kanál."
          />
          <ReasonCard
            icon={Database}
            title="Spoločný náklad < Semrush"
            body="KWFinder Premium (€49/mes) + TopRankPulse Pro (€49/mes) = €98/mes. Pokrýva klasický aj AI SEO stack — stále o 55% lacnejšie ako Semrush Guru (€220/mes)."
          />
          <ReasonCard
            icon={Globe}
            title="Oba sú slovenské"
            body="Mangools (Prešov → globál) a TopRankPulse (Bratislava, SK/CZ fokus) sú obe slovenské firmy. Dva uhly pokrytia zo spoločnej scény."
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-12 max-w-4xl mx-auto" data-testid="compare-faq">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6">Časté otázky</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={f.q} className="rounded-xl border border-white/10 bg-white/5 overflow-hidden" data-testid={`compare-faq-${i}`}>
              <button
                type="button"
                onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-white/5"
                data-testid={`compare-faq-toggle-${i}`}
              >
                <span className="font-bold text-base">{f.q}</span>
                <span className={`text-gray-400 transition-transform ${faqOpen === i ? "rotate-45" : ""}`}>+</span>
              </button>
              {faqOpen === i && (
                <div className="px-5 pb-5 text-sm text-gray-300 leading-relaxed" data-testid={`compare-faq-answer-${i}`}>
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 py-16 max-w-4xl mx-auto" data-testid="compare-final-cta">
        <div className="rounded-2xl border border-[#a855f7]/40 bg-gradient-to-br from-[#a855f7]/20 via-[#a855f7]/5 to-transparent p-8 sm:p-10">
          <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-[#FFE600] mb-3 font-['JetBrains_Mono']">
            <ShieldCheck className="w-4 h-4" />
            <span>14-dňová záruka vrátenia peňazí</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk'] mb-3 leading-tight">
            KWFinder ti ukáže kľúčové slová. ChatGPT ich ale neberie do úvahy.
          </h2>
          <p className="text-gray-300 mb-6 max-w-xl">
            Za <strong className="text-white">€7</strong> ti za 10 minút ukážeme kde ťa cituje
            ChatGPT, Perplexity, Gemini. 14-dňová záruka vrátenia peňazí.
          </p>
          <Link
            to="/snapshot"
            onClick={() => trackEvent("compare_final_cta_click", { destination: "/snapshot", competitor: "mangools" })}
            className="inline-flex items-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-white font-black px-7 py-4 rounded-lg text-lg transition-all"
            data-testid="compare-final-cta-btn"
          >
            Spustiť audit za €7 <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-[11px] text-gray-500 mt-4" data-testid="compare-final-cta-trust">
            🇸🇰 Prevádzkuje RESTOB s.r.o., Bratislava · GDPR-compliant · dáta neopúšťajú EU
          </p>
        </div>
      </section>
    </div>
  );
}

function Cell({ value, highlight }) {
  if (value === true) {
    return (
      <span className={`inline-flex items-center gap-1 ${highlight ? "text-emerald-400" : "text-emerald-500"}`}>
        <Check className="w-4 h-4" /> Áno
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1 text-gray-500">
        <X className="w-4 h-4" /> Nie
      </span>
    );
  }
  return <span>{value}</span>;
}

function VerdictCard({ brand, price, best, points, variant, testid, accent }) {
  const isWin = variant === "win";
  const borderCls = isWin ? "border-[#a855f7]/40" : "border-white/10";
  const bgCls = isWin ? "bg-gradient-to-br from-[#a855f7]/15 to-transparent" : "bg-white/5";
  return (
    <div className={`rounded-xl border ${borderCls} ${bgCls} p-6`} data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-xl font-black font-['Cabinet_Grotesk']" style={accent ? { color: accent } : undefined}>{brand}</h3>
        {isWin && (
          <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            Odporúčané pre AI SEO
          </span>
        )}
      </div>
      <p className="text-sm text-gray-300 mb-1">{price}</p>
      <p className="text-xs text-gray-400 mb-4 leading-relaxed">{best}</p>
      <ul className="space-y-2">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-2 text-sm text-gray-200">
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0`} style={{ color: accent || "#6b7280" }} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReasonCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-5">
      <Icon className="w-5 h-5 text-[#FFE600] mb-3" />
      <h3 className="font-bold text-base mb-1">{title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed">{body}</p>
    </div>
  );
}
